<template>
  <div class="error-codes-container">
    <div class="page-header">
      <div class="header-title">
        <el-icon class="title-icon"><Settings /></el-icon>
        <span>{{ $t('ErrorCodes.completeFaultCodeList') }}</span>
      </div>
    </div>

    <div class="device-cards">
      <div 
        v-for="device in deviceTypes" 
        :key="device.type"
        class="device-card"
        :class="{ 'active': selectedDevice === device.type }"
        @click="selectDevice(device.type)"
      >
        <div class="device-icon-wrapper" :class="device.bgClass">
          <component :is="device.icon" class="device-icon" />
        </div>
        <div class="device-info">
          <span class="device-name">{{ device.name }}</span>
          <span class="fault-count">{{ device.count }} {{ $t('ErrorCodes.faultCodes') }}</span>
        </div>
      </div>
    </div>

    <div class="filter-section">
      <span class="filter-label">{{ $t('ErrorCodes.filterByCategory') }}:</span>
      <el-select 
        v-model="selectedCategory" 
        class="category-select"
        @change="handleCategoryChange"
      >
        <el-option 
          v-for="category in categories" 
          :key="category.value" 
          :label="category.label" 
          :value="category.value"
        />
      </el-select>
    </div>

    <div class="summary-section">
      <span class="summary-text">
        {{ $t('ErrorCodes.totalFaultCodes') }}: {{ totalFaultCodes }} 
        | E-XX: {{ eCount }} 
        | W-XX: {{ wCount }} 
        | N-XX: {{ nCount }}
      </span>
    </div>

    <div class="fault-codes-grid">
      <div 
        v-for="fault in filteredFaultCodes" 
        :key="fault.code"
        class="fault-code-item"
        :class="[getFaultClass(fault.code), { 'selected': selectedFault && selectedFault.code === fault.code }]"
        @click="showFaultDetail(fault)"
      >
        {{ fault.code }}
      </div>
    </div>

    <div class="fault-detail-section" :class="{ 'has-selection': selectedFault }">
      <div class="detail-header">
        <span class="detail-title">{{ $t('ErrorCodes.faultCodeDecoder') }}</span>
        <span class="detail-desc">{{ $t('ErrorCodes.inputErrorToDiagnose') }}</span>
      </div>
      
      <div class="detail-body">
        <div class="input-section">
          <span class="input-label">{{ $t('ErrorCodes.errorCode') }}</span>
          <el-input 
            v-model="searchCode" 
            class="search-input"
            :placeholder="$t('ErrorCodes.searchPlaceholder')"
            @keyup.enter="decodeFault"
          />
          <span class="input-hint">{{ $t('ErrorCodes.inputHint') }}</span>
          <el-button 
            class="decode-btn" 
            type="primary" 
            @click="decodeFault"
          >
            {{ $t('ErrorCodes.decodeError') }}
          </el-button>
        </div>

        <div v-if="selectedFault" class="result-section">
          <div class="fault-title">{{ selectedFault.title }}</div>
          <div class="fault-description">{{ selectedFault.description }}</div>
          
          <div v-if="selectedFault.causes && selectedFault.causes.length > 0" class="possible-causes">
            <span class="section-label">{{ $t('ErrorCodes.possibleCauses') }}:</span>
            <ul>
              <li v-for="(cause, index) in selectedFault.causes" :key="index">{{ cause }}</li>
            </ul>
          </div>

          <div v-if="selectedFault.solutions && selectedFault.solutions.length > 0" class="solutions">
            <div 
              v-for="(solution, index) in selectedFault.solutions" 
              :key="index"
              class="solution-item"
            >
              <span class="solution-type" :class="solution.type">{{ solution.type === 'hub' ? '轮毂电机' : solution.type === 'mid' ? '中置电机' : '显示器' }}</span>
              <span class="solution-label">{{ solution.label }}</span>
              <ul>
                <li v-for="(step, stepIndex) in solution.steps" :key="stepIndex">
                  {{ stepIndex + 1 }}. {{ step }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { Setting, Wallet, Cpu, Bell, Monitor, Search } from '@element-plus/icons-vue';
import { FaultCodeService } from '@/services/faultCodeService';

export default {
  name: 'ErrorCodes',
  components: {
    Setting,
    Wallet,
    Cpu,
    Bell,
    Monitor,
    Search,
  },
  data() {
    return {
      selectedDevice: 'motor_controller',
      selectedCategory: 'all',
      selectedFault: null,
      searchCode: '',
      
      deviceTypeIcons: {
        battery: markRaw(Wallet),
        motor_controller: markRaw(Cpu),
        sensor: markRaw(Bell),
        communication: markRaw(Monitor),
      },
      
      deviceTypeBgClasses: {
        battery: 'bg-green',
        motor_controller: 'bg-orange',
        sensor: 'bg-blue',
        communication: 'bg-purple',
      },
    };
  },
  computed: {
    deviceTypes() {
      const types = FaultCodeService.getDeviceTypes(this.$i18n.locale);
      return Object.keys(types).map(type => ({
        type,
        name: types[type],
        count: FaultCodeService.getFaultsByDevice(type, this.$i18n.locale).length,
        icon: this.deviceTypeIcons[type],
        bgClass: this.deviceTypeBgClasses[type],
      }));
    },
    
    categories() {
      const cats = FaultCodeService.getCategories(this.$i18n.locale);
      return [
        { value: 'all', label: cats.all },
        { value: 'E', label: 'E-XX' },
        { value: 'W', label: 'W-XX' },
        { value: 'N', label: 'N-XX' },
      ];
    },
    
    currentFaultCodes() {
      return FaultCodeService.getFaultsByDevice(this.selectedDevice, this.$i18n.locale);
    },
    
    filteredFaultCodes() {
      if (this.selectedCategory === 'all') {
        return this.currentFaultCodes;
      }
      return this.currentFaultCodes.filter(fault => 
        fault.code.startsWith(this.selectedCategory)
      );
    },
    
    totalFaultCodes() {
      return this.currentFaultCodes.length;
    },
    
    eCount() {
      return this.currentFaultCodes.filter(f => f.code.startsWith('E')).length;
    },
    
    wCount() {
      return this.currentFaultCodes.filter(f => f.code.startsWith('W')).length;
    },
    
    nCount() {
      return this.currentFaultCodes.filter(f => f.code.startsWith('N')).length;
    },
  },
  methods: {
    selectDevice(deviceType) {
      this.selectedDevice = deviceType;
      this.selectedCategory = 'all';
    },
    
    handleCategoryChange() {
    },
    
    getFaultClass(code) {
      if (code.startsWith('E')) return 'fault-e';
      if (code.startsWith('W')) return 'fault-w';
      if (code.startsWith('N')) return 'fault-n';
      return '';
    },
    
    showFaultDetail(fault) {
      this.selectedFault = fault;
    },
    
    closeFaultDetail() {
      this.selectedFault = null;
    },
    
    decodeFault() {
      if (!this.searchCode.trim()) {
        return;
      }
      
      const input = this.searchCode.trim().toUpperCase();
      let code = input;
      
      if (/^\d+$/.test(input)) {
        code = 'E-' + input.padStart(2, '0');
      } else if (/^[EWN]\d{1,2}$/.test(input)) {
        code = input.charAt(0) + '-' + input.slice(1).padStart(2, '0');
      }
      
      const fault = FaultCodeService.getFaultByCode(code, this.$i18n.locale);
      
      if (fault) {
        this.selectedFault = fault;
      } else {
        this.$message.error(this.$t('ErrorCodes.decodeNotFound'));
      }
    },
  },
};
</script>

<style scoped>
.error-codes-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  background-color: #fff;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.header-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.title-icon {
  margin-right: 10px;
  color: #666;
}

.device-cards {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.device-card {
  flex: 1;
  min-width: 200px;
  max-width: 280px;
  background-color: #fff;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.device-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.device-card.active {
  border-color: #009a44;
}

.device-icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.bg-green {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}

.bg-orange {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
}

.bg-blue {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
}

.bg-purple {
  background: linear-gradient(135deg, #f3e5f5, #e1bee7);
}

.device-icon {
  font-size: 24px;
}

.bg-green .device-icon { color: #4caf50; }
.bg-orange .device-icon { color: #ff9800; }
.bg-blue .device-icon { color: #2196f3; }
.bg-purple .device-icon { color: #9c27b0; }

.device-info {
  text-align: center;
}

.device-name {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.fault-count {
  font-size: 14px;
  color: #666;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.filter-label {
  font-size: 14px;
  color: #666;
}

.category-select {
    width: 150px;
  }

  .summary-section {
  margin-bottom: 20px;
}

.summary-text {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.fault-codes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  max-width: 1200px;
}

.fault-code-item {
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.fault-code-item:hover {
  transform: scale(1.05);
  border-color: #009a44;
}

.fault-e {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  color: #e65100;
}

.fault-w {
  background: linear-gradient(135deg, #fffde7, #fff9c4);
  color: #f9a825;
}

.fault-n {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  color: #1976d2;
}

.fault-code-item.selected {
  border-color: #009a44;
  transform: scale(1.05);
}

.fault-detail-section {
  margin-top: 30px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.detail-header {
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.detail-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.detail-body {
  padding: 24px;
}

.input-section {
  margin-bottom: 24px;
}

.input-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #c8e6c9;
  border-radius: 4px;
  background-color: #e8f5e9;
  font-size: 16px;
  color: #333;
  margin-bottom: 8px;
}

.search-input::placeholder {
  color: #999;
}

.decode-btn {
  background-color: #ff9800;
  border-color: #ff9800;
  padding: 10px 24px;
  border-radius: 4px;
  font-weight: 600;
  margin-top: 8px;
}

.decode-btn:hover {
  background-color: #f57c00;
  border-color: #f57c00;
}

.code-display {
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
}

.input-hint {
  display: block;
  font-size: 12px;
  color: #999;
}

.result-section {
  background-color: #f8fafc;
  border-radius: 8px;
  padding: 20px;
}

.fault-title {
  font-size: 18px;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 8px;
}

.fault-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

.possible-causes {
  margin-bottom: 16px;
}

.section-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.possible-causes ul {
  margin: 0;
  padding-left: 20px;
}

.possible-causes li {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  list-style-type: disc;
}

.solutions {
  margin-top: 16px;
}

.solution-item {
  margin-bottom: 16px;
}

.solution-item:last-child {
  margin-bottom: 0;
}

.solution-type {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  margin-right: 8px;
}

.solution-type.hub {
  background-color: #1976d2;
}

.solution-type.mid {
  background-color: #4caf50;
}

.solution-type.display {
  background-color: #9c27b0;
}

.solution-label {
  font-weight: 600;
  color: #333;
}

.solution-item ul {
  margin: 8px 0 0 20px;
  padding: 0;
}

.solution-item li {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  list-style-type: decimal;
}
</style>